import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import type {PipelineDependencyId, PipelineDependencySettings} from '../../types'
import {getPipelineDependency} from '../EditPipelinePage.utils'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty} from './EditPipelinePage.slices.utils'

export const addDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    dependencyId: string
    value?: PipelineDependencySettings | null
  }>
> = (state, action) => {
  const {pipelineId, dependencyId, value} = action.payload

  const {draft} = state[pipelineId] ?? {}
  if (
    draft != null &&
    !draft.settings.dependencies?.some(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
  ) {
    const dependency =
      value == null ? (dependencyId as PipelineDependencyId) : {[dependencyId]: value}
    draft.settings.dependencies ??= []
    draft.settings.dependencies?.push(dependency)
    state[pipelineId]!.added ??= {}
    state[pipelineId]!.added.dependencies ??= {}
    state[pipelineId]!.added.dependencies[dependencyId] = true
  }
}

export const deleteDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId}>
> = (state, action) => {
  const {pipelineId, dependencyId} = action.payload
  state[pipelineId] ??= {}

  if (state[pipelineId].added?.dependencies?.[dependencyId] != null) {
    const dependencies = state[pipelineId]?.draft?.settings?.dependencies
    const index = dependencies?.findIndex(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
    if (dependencies && index != null && index !== -1) {
      state[pipelineId]?.draft?.settings?.dependencies?.splice(index, 1)
    }
    delete state[pipelineId]?.added?.dependencies?.[dependencyId]
    deleteIfEmpty(state[pipelineId]!.added, 'dependencies')
    deleteIfEmpty(state[pipelineId], 'added')
    deleteIfEmpty(state[pipelineId]!.draft?.settings, 'dependencies')
  } else {
    state[pipelineId].deleted ??= {}
    state[pipelineId].deleted!.dependencies ??= []
    state[pipelineId].deleted!.dependencies!.push(dependencyId)
  }
}

export const updateDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    dependencyId: PipelineDependencyId
    newDepenedencyId?: PipelineDependencyId
    value?: PipelineDependencySettings | null
  }>
> = (state, action) => {
  const {pipelineId, dependencyId, newDepenedencyId, value} = action.payload
  const {draft} = state[pipelineId] ?? {}
  if (draft != null) {
    draft.settings.dependencies ??= []
    const index = draft.settings.dependencies.findIndex(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
    if (index === -1) {
      return
    }
    if (value) {
      draft.settings.dependencies[index] = {
        [newDepenedencyId ?? dependencyId]: value,
      }
    } else {
      draft.settings.dependencies[index] = newDepenedencyId ?? dependencyId
    }
  }
}

export const applyDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId}>
> = (state, action) => {
  const {pipelineId, dependencyId} = action.payload

  if (state[pipelineId]?.added?.dependencies?.[dependencyId] != null) {
    delete state[pipelineId]?.added?.dependencies?.[dependencyId]
    deleteIfEmpty(state[pipelineId]!.added, 'dependencies')
    deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const restoreDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId}>
> = (state, action) => {
  const {pipelineId, dependencyId} = action.payload
  const {deleted} = state[pipelineId] ?? {}
  if (deleted?.dependencies?.includes(dependencyId)) {
    deleted.dependencies = deleted.dependencies.filter(deletedId => deletedId !== dependencyId)
    deleteIfEmpty(deleted, 'dependencies')
    deleteIfEmpty(state[pipelineId], 'deleted')
  }
}
